/**
 * This is a forked version of the NDS internal Autocomplete popover content component,
 * which has been modified to support non-selectable "headings" in its
 * composition, as well as to support searching by a "searchValue" prop on the
 * select child Items.
 *
 * The important change in this file relative to the original is that
 * the `searchValue` prop is used in the `itemToString` function to allow
 * searching by a custom string rather than just the `value` prop.
 * This allows for more flexible searching capabilities.
 *
 * There is also a new `isSelectable` function to determine if an item is a selectable AutoCompleteItem
 * versus a non-selectable heading. Headings are rendered without click handlers or highlighting.
 */

import React, { useState, useEffect } from "react";
import cc from "classcat";
import { useCombobox } from "downshift";
import { Row, TextInput } from "@narmi/design_system";

export type AutoCompleteItem = React.ReactElement<any>;

interface AutoCompleteProps {
  /** Input label */
  label: string;
  /** ComboboxModal.Item children */
  items: AutoCompleteItem[];
  selectedItem: AutoCompleteItem | null;
  /** Selection change event callback */
  onChange?: (item: AutoCompleteItem) => void;
  /** Input change event callback */
  onInputChange?: (e: string) => void;
  /** Optional pinned footer content; use for action buttons */
  footerContent?: React.ReactNode;
}

const itemToString = (item: AutoCompleteItem) => item.props.searchValue || item.props.value || "";

const filterItems = (items: AutoCompleteItem[], inputValue: string) =>
  items.filter((item) => itemToString(item).toLowerCase().includes(inputValue.toLowerCase()));

/**
 * A simplified Combobox component that renders in a Popup.
 * Useful for inline editing of things like table items where we want to
 * provide an autocomplete of possible values.
 *
 * Allows single selection only. The selected value is controlled, allowing the selection to render in the trigger.
 * trigger area.
 */
const AutoComplete = ({
  label,
  items: allItems,
  selectedItem: selectedItemProp,
  onChange = () => {},
  onInputChange = () => {},
  footerContent,
}: AutoCompleteProps) => {
  const isSelectable = (item: React.ReactElement<any>) => "value" in item.props;
  const [items, setItems] = useState(allItems);

  // update if items change programmatically
  useEffect(() => {
    setItems(allItems);
  }, [setItems, allItems]);

  const { selectedItem, highlightedIndex, getMenuProps, getInputProps, getItemProps } = useCombobox(
    {
      items,
      initialSelectedItem: selectedItemProp,
      initialInputValue: "",
      onInputValueChange({ inputValue }) {
        setItems(inputValue?.length > 0 ? filterItems(allItems, inputValue) : allItems);
        onInputChange(inputValue);
      },
      onSelectedItemChange({ selectedItem }) {
        if (selectedItem) {
          onChange(selectedItem);
        }
      },
      itemToString(item: React.ReactNode) {
        // @ts-expect-error downshift has an odd type for `item` here
        return itemToString(item);
      },
    }
  );

  return (
    <div className={cc(["nds-autocompleteModal-autocomplete", "rounded--all"])}>
      <div className="nds-autocompleteModal-input">
        <TextInput label={label} {...getInputProps()} />
      </div>
      <ul className="nds-autocompleteModal-menu list--reset" {...getMenuProps()}>
        {items.length === 0 && !selectedItem && (
          <li className="nds-autocompleteModal-item padding--x--s padding--y--xs">
            No results found
          </li>
        )}
        {items.map((item, index) => {
          if (!isSelectable(item)) {
            return (
              <li key={`${item}-${index}`} className="padding--x--s padding--y--xs">
                {item}
              </li>
            );
          }

          return (
            <li
              className={cc([
                "nds-autocompleteModal-item",
                "padding--x--s padding--y--xs",
                {
                  "nds-autocompleteModal-item--highlighted": highlightedIndex === index,
                },
              ])}
              key={`${item?.props.value}-${index}`}
              {...getItemProps({ item, index })}
            >
              <Row>
                <Row.Item>{item}</Row.Item>
                {selectedItem && selectedItem.props.value === item.props.value && (
                  <Row.Item as="span" shrink>
                    <span className="narmi-icon-check fontSize--l fontWeight--bold" />
                  </Row.Item>
                )}
              </Row>
            </li>
          );
        })}
      </ul>
      {footerContent && (
        <div className="nds-autocompleteModal-footer padding--all--s border--top">
          {footerContent}
        </div>
      )}
    </div>
  );
};

export default AutoComplete;
